import { authenticatedFetch } from './authApi';
import { getEnv } from '../utils/env';

export interface OnlinePortalUser {
  displayName: string;
  email: string;
  connections: number;
  lastActionAt?: string | null;
  lastActionSecondsAgo?: number | null;
}

export const fetchOnlinePortalUsers = async (signal?: AbortSignal): Promise<OnlinePortalUser[]> => {
  const baseUrl = getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
  if (!baseUrl) throw new Error('VITE_AI_API_URL is not configured');

  const response = await authenticatedFetch(`${baseUrl}/api/v1/stream/global/presence-users`, { signal, cache: 'no-store' });
  if (!response.ok) throw new Error(`Presence users request failed (${response.status})`);
  const payload = await response.json();
  if (!Array.isArray(payload?.data) || !payload.data.every((user: OnlinePortalUser) =>
    user && typeof user.displayName === 'string' && typeof user.email === 'string'
    && Number.isInteger(user.connections) && user.connections > 0
  )) throw new Error('Invalid presence response');
  return payload.data;
};

/** Refresh only while the administrator's visible popover is open. */
export function startOnlineUsersPolling(
  onUsers: (users: OnlinePortalUser[]) => void,
  onError: () => void,
  { target = document, fetchUsers = fetchOnlinePortalUsers }: {
    target?: Document; fetchUsers?: typeof fetchOnlinePortalUsers;
  } = {},
): () => void {
  const controller = new AbortController();
  let inFlight = false;
  const refresh = async () => {
    if (controller.signal.aborted || inFlight || target.visibilityState !== 'visible') return;
    inFlight = true;
    const request = new AbortController();
    const cancel = () => request.abort();
    controller.signal.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 8_000);
    try {
      const users = await fetchUsers(request.signal);
      if (!controller.signal.aborted) {
        if (request.signal.aborted) onError();
        else onUsers(users);
      }
    } catch {
      if (!controller.signal.aborted) onError();
    } finally {
      clearTimeout(timeout);
      controller.signal.removeEventListener('abort', cancel);
      inFlight = false;
    }
  };
  void refresh();
  const timer = setInterval(() => { void refresh(); }, 10_000);
  target.addEventListener('visibilitychange', refresh);
  return () => {
    controller.abort();
    clearInterval(timer);
    target.removeEventListener('visibilitychange', refresh);
  };
}
